import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";

interface Feature {
  icon: string;
  color: string;
  tag: string;
  title: string;
  desc: string;
  bullets: string[];
  image: string;
}

const features: Feature[] = [
  {
    icon: "ri-bookmark-3-fill",
    color: "bg-primary-100 text-primary-800",
    tag: "书架收藏",
    title: "收藏，让喜欢的一目了然",
    desc: "无论是连载神作还是冷门宝藏，一键加入书架。书架支持按分类、作者、更新状态灵活整理，你的追更清单永远清爽。",
    bullets: ["一键收藏与取消收藏，即时生效", "按分类、作者、状态智能整理", "收藏夹云同步，多设备一致"],
    image:
      "https://readdy.ai/api/search-image?query=cozy%20illustration%20of%20a%20digital%20bookshelf%20with%20neatly%20arranged%20colorful%20manga%20books%20and%20bookmark%20ribbons%2C%20warm%20coral%20and%20teal%20palette%2C%20flat%20vector%20style%2C%20clean%20background&width=900&height=700&seq=feature-shoucang&orientation=landscape",
  },
  {
    icon: "ri-notification-3-fill",
    color: "bg-accent-100 text-accent-800",
    tag: "更新订阅",
    title: "订阅，更新绝不错过",
    desc: "订阅正在追更的作品，一旦有更新，首页与个人中心即刻提醒。追更效率直接拉满，再也不用每天刷新等更新。",
    bullets: ["更新第一时间推送提醒", "订阅列表集中管理连载", "可设置专属追更提醒偏好"],
    image:
      "https://readdy.ai/api/search-image?query=friendly%20illustration%20of%20a%20phone%20with%20a%20glowing%20notification%20bell%20and%20upcoming%20comic%20update%20alerts%2C%20warm%20teal%20and%20amber%20palette%2C%20flat%20vector%20style%2C%20clean%20background&width=900&height=700&seq=feature-dingyue&orientation=landscape",
  },
  {
    icon: "ri-download-cloud-2-fill",
    color: "bg-secondary-100 text-secondary-800",
    tag: "离线缓存",
    title: "离线，没网也能看",
    desc: "通勤路上、地铁深处、偏远地区……把想看的漫画提前缓存，随时随地离线畅读。缓存管理与空间占用，全由你掌控。",
    bullets: ["按章节或整部自由缓存", "缓存进度与在线同步", "一键清理，绝不拖累手机"],
    image:
      "https://readdy.ai/api/search-image?query=illustration%20of%20a%20phone%20displaying%20a%20downloaded%20comic%20with%20a%20cloud%20download%20icon%20offline%20mode%20badge%2C%20warm%20amber%20and%20coral%20palette%2C%20flat%20vector%20style%2C%20clean%20background&width=900&height=700&seq=feature-lixian&orientation=landscape",
  },
  {
    icon: "ri-history-fill",
    color: "bg-primary-100 text-primary-800",
    tag: "历史记录",
    title: "历史，随时接着看",
    desc: "阅读进度自动记录，即使中途被打断、更换设备，也能从上次停下的地方无缝续读。你的每一话进度，都被好好保存。",
    bullets: ["自动记录每部作品的阅读进度", "换设备登录，进度跨端同步", "历史记录一目了然，方便回看"],
    image:
      "https://readdy.ai/api/search-image?query=clean%20illustration%20of%20a%20timeline%20of%20reading%20history%20with%20a%20resume%20playback%20icon%20across%20multiple%20devices%2C%20warm%20coral%20and%20teal%20palette%2C%20flat%20vector%20style%2C%20clean%20background&width=900&height=700&seq=feature-lishi&orientation=landscape",
  },
  {
    icon: "ri-star-smile-fill",
    color: "bg-accent-100 text-accent-800",
    tag: "个性化推荐",
    title: "推荐，更懂你想看的",
    desc: "基于你的收藏、订阅与阅读习惯，为你智能推荐可能喜欢的新番与宝藏作品。每次打开首页，都有惊喜等在前面。",
    bullets: ["根据阅读偏好持续优化推荐", "每周更新个性化新番推荐", "可对推荐结果一键反馈调优"],
    image:
      "https://readdy.ai/api/search-image?query=delightful%20illustration%20of%20a%20sparkling%20recommendation%20feed%20with%20a%20smiling%20star%20and%20a%20catalog%20of%20comic%20covers%2C%20warm%20teal%20and%20coral%20palette%2C%20flat%20vector%20style%2C%20clean%20background&width=900&height=700&seq=feature-tuijian&orientation=landscape",
  },
];

const steps = [
  {
    icon: "ri-login-box-line",
    title: "登录账号",
    desc: "使用邮箱或手机号注册登录，收藏与历史即可云同步。",
  },
  {
    icon: "ri-settings-3-line",
    title: "调整阅读设置",
    desc: "在「阅读设置」中按需开启自动续读、更新提醒、离线缓存策略。",
  },
  {
    icon: "ri-rocket-2-line",
    title: "开始享受",
    desc: "收藏、订阅、离线、历史与推荐全部就位，放心追更。",
  },
];

export default function Features() {
  return (
    <div>
      {/* 面包屑 + 页头 */}
      <section className="bg-background-100 py-8">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">阅读功能</span>
          </nav>
          <div className="mt-4 max-w-2xl">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">
              个性化阅读体验
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              新新漫画网收藏、订阅、离线、历史记录与个性化推荐全支持。本页带你完整了解每一项阅读功能与设置用法，追更更顺手，看书更自在。
            </p>
          </div>
        </div>
      </section>

      {/* 功能详情（交替布局） */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-14">
          {features.map((f, i) => (
            <article
              key={f.title}
              className={`flex flex-col items-center gap-8 lg:gap-12 ${
                i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
              }`}
            >
              <div className="w-full lg:w-1/2">
                <img
                  src={f.image}
                  alt={`${f.title}功能示意图`}
                  className="w-full rounded-lg border border-background-200/70 object-cover"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${f.color}`}
                >
                  <i className={f.icon}></i>
                  {f.tag}
                </span>
                <h2 className="mt-4 font-heading text-2xl text-foreground-950">{f.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-foreground-700">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* 使用步骤 */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            center
            eyebrow="三步上手"
            title="阅读设置与功能用法"
            desc="简单三步，把新新漫画网调教成最适合你的阅读工具。"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {steps.map((s, i) => (
              <div
                key={s.title}
                className="relative rounded-lg border border-background-200/70 bg-background-50 p-6"
              >
                <span className="absolute right-4 top-4 font-heading text-4xl text-background-200">
                  0{i + 1}
                </span>
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className={`${s.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 相关 FAQ + CTA */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="你可能想问"
              title="关于阅读功能的常见问题"
              desc="这里汇集了大家在使用阅读功能时最常遇到的疑问。"
            />
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              查看更多常见问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="flex flex-col gap-3">
            <details className="group rounded-lg border border-background-200/70 bg-background-50 p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                收藏、订阅、离线需要付费吗？
                <i className="ri-add-line text-secondary-600 transition group-open:rotate-45"></i>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                这些均为基础功能，注册登录后即可免费使用。部分高规格离线缓存额度与去广告权益可能提供付费会员，具体以页面说明为准。
              </p>
            </details>
            <details className="group rounded-lg border border-background-200/70 bg-background-50 p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                离线缓存的漫画会过期吗？
                <i className="ri-add-line text-secondary-600 transition group-open:rotate-45"></i>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                不会。已缓存的内容在有效期内可随时离线阅读，到期前也会有清晰提示，方便你及时刷新缓存，不影响追更体验。
              </p>
            </details>
            <details className="group rounded-lg border border-background-200/70 bg-background-50 p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                如何让推荐更符合我的口味？
                <i className="ri-add-line text-secondary-600 transition group-open:rotate-45"></i>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                多收藏、订阅你喜欢的作品，并多阅读几话，推荐算法就会持续学习你的偏好。你也可以对推荐结果直接点「感兴趣」或「不感兴趣」快速调优。
              </p>
            </details>
          </div>
        </div>

        <div className="mt-14 rounded-lg bg-primary-500 px-6 py-10 text-center">
          <h2 className="font-heading text-2xl text-background-50">准备好享受个性化阅读了吗？</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-primary-100">
            收藏、订阅、离线、历史记录与个性化推荐，现在就去开通你的专属追更体验。
          </p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/ranking"
              className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-medium text-primary-900 transition hover:bg-background-100"
            >
              去排行榜选书
            </Link>
            <Link
              to="/faq"
              className="whitespace-nowrap rounded-md border border-background-50/50 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              遇到问题看帮助
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}